import {
  CbhIcon,
  DotSeparatedList,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ReviewsSummary,
  Text,
} from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { type GeoLocation } from "@src/appV2/Location";
import { useGetRatingAggregates } from "@src/appV2/redesign/Workplace/Review/api/useGetRatingAggregates";
import { minutesToMilliseconds } from "date-fns";

import { useDistanceToWorkplace } from "../Workplace/useDistanceToWorkplace";

interface ShiftWorkplaceListItemProps {
  workplaceName: string;
  workplaceType: string;
  workplaceId: string;
  workplaceLocation: GeoLocation;
  hideCallToActionIcon?: boolean;
  onClick?: () => void;
}

export function ShiftWorkplaceListItem(props: ShiftWorkplaceListItemProps) {
  const {
    workplaceName,
    workplaceType,
    workplaceId,
    workplaceLocation,
    hideCallToActionIcon,
    onClick,
  } = props;

  const { formattedDistance } = useDistanceToWorkplace({
    workplaceGeoLocation: workplaceLocation,
  });

  const { data: ratingBreakdowns } = useGetRatingAggregates(workplaceId, {
    enabled: true,
    staleTime: minutesToMilliseconds(5),
  });

  return (
    <ListItemButton
      hideCallToActionIcon={hideCallToActionIcon}
      onClick={() => {
        onClick?.();
      }}
    >
      <ListItemIcon iconType="map-pin" />
      <ListItemText
        disableTypography
        primary={
          <Text semibold variant="h6" sx={{ textWrap: "balance" }}>
            {workplaceName}
          </Text>
        }
        secondary={
          <Stack spacing={4} sx={{ marginTop: 2 }}>
            <Text variant="body2">
              <DotSeparatedList inline sx={{ width: "100%" }}>
                {workplaceType}
                {isDefined(formattedDistance) && (
                  <>
                    <CbhIcon type="car" size="small" />
                    <Text variant="body2">{` ${formattedDistance} from home`}</Text>
                  </>
                )}
              </DotSeparatedList>
            </Text>

            <ReviewsSummary
              rating={ratingBreakdowns?.avgRating ?? 0}
              ratingsCount={ratingBreakdowns?.numberOfRatings ?? 0}
              size="small"
            />
          </Stack>
        }
      />
    </ListItemButton>
  );
}
